import * as R from 'remeda';
import { getToolName, isToolUIPart, type UIMessage } from 'ai';

const HIDDEN_TOOLS = new Set(['create_task', 'update_task', 'list_tasks']);

type MessageParts = UIMessage['parts'];

const isTextPart = (
  p: MessageParts[number]
): p is { type: 'text'; text: string } => p.type === 'text';

export const getTextContent = (parts: MessageParts): string =>
  parts
    .filter(isTextPart)
    .map(p => p.text)
    .join('');

/**
 * The assistant's final spoken text, dropping the "Let me fetch…" narration
 * emitted between tool calls. Parts are split into segments at every (visible)
 * tool boundary and we return the last segment that actually contains text.
 *
 * Taking the last *non-empty* segment (rather than everything after the last
 * tool boundary) keeps the answer intact when the message ends with a tool
 * call — a trailing tool would otherwise leave an empty segment and swallow
 * the answer. Joining within a segment keeps text whole when interleaved
 * reasoning splits it across parts. Hidden task-management tools don't start
 * a new segment, so a trailing `update_task` can't split off the answer.
 */
export const getFinalTextContent = (parts: MessageParts): string => {
  const isSegmentBoundary = (p: MessageParts[number]): boolean =>
    isToolUIPart(p) && !isHiddenTool(getToolName(p));

  return R.pipe(
    parts,
    R.reverse(),
    // Skip trailing non-text parts (e.g. a closing tool call) so a message that
    // ends with a tool call falls back to the previous spoken segment.
    R.dropWhile(p => !(isTextPart(p) && p.text !== '')),
    // Stay within that final segment: stop at the tool boundary opening it.
    R.takeWhile(p => !isSegmentBoundary(p)),
    R.filter(isTextPart),
    R.reverse(),
    R.map(p => p.text),
    R.join('')
  );
};

export const getReasoningContent = (parts: MessageParts): string =>
  parts
    .filter(
      (p): p is { type: 'reasoning'; text: string } => p.type === 'reasoning'
    )
    .map(p => p.text.trim())
    .filter(Boolean)
    .join('\n\n');

export const isHiddenTool = (toolName: string): boolean =>
  HIDDEN_TOOLS.has(toolName);

/**
 * Providers may strip reasoning content for safety reasons, leaving an empty
 * reasoning part with a `redactedContent` marker in provider metadata. The
 * metadata is keyed by provider name (e.g. `bedrock`), so we look for the
 * marker under any provider rather than hardcoding one.
 */
export const hasRedactedReasoning = (parts: MessageParts): boolean =>
  parts.some(
    p =>
      p.type === 'reasoning' &&
      p.text.trim() === '' &&
      Object.values(p.providerMetadata ?? {}).some(
        metadata => metadata['redactedContent'] !== undefined
      )
  );
